/* eslint-disable react/no-unstable-nested-components */

import { AssignmentEdge, CompletionStatus } from '@/client/services/api/graphql/gql/graphql';
import { Box, Checkbox, HStack, Progress, Text } from '@chakra-ui/react';
import { ColumnDef, PaginationState, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import AssignmentsTable from '../tables/AssignmentsTable';
import WarmNeutralTableActionsMenu from '../tables/WarmNeutralTableActionsMenu';
import { format } from 'date-fns';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAdminAssignmentDetailsStore } from '../state/adminAssignmentDetailsStore';
import { useListAssignmentRecipients } from '../hooks/useListAssignmentRecipients';
import { useTranslation } from 'react-i18next';
import { useWorkflowActions } from '../hooks/useWorkflowActions';

export default function AssignmentDetailsRecipientsView() {
  const { t } = useTranslation();
  const { id, instanceId } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const isInstance = pathname.includes('instance');

  const { recipientSelection, setRecipientSelection } = useAdminAssignmentDetailsStore();
  const { isAllowed } = useAccessHelper();

  const completionStatusTranslationMap = {
    [CompletionStatus.CompletedLate]: t('admin.assignments.completionStatus.completeLate'),
    [CompletionStatus.CompletedOnTime]: t('admin.assignments.completionStatus.completeOnTime'),
    [CompletionStatus.InProgress]: t('admin.assignments.completionStatus.inProgress'),
    [CompletionStatus.NotStarted]: t('admin.assignments.completionStatus.notStarted'),
    [CompletionStatus.Overdue]: t('admin.assignments.completionStatus.incompleteOverdue'),
    [CompletionStatus.PendingOnTime]: t('admin.assignments.completionStatus.incompleteOnTime'),
    [CompletionStatus.Unassigned]: t('admin.assignments.completionStatus.unassigned'),
  };

  const permissions = {
    edit: isAllowed(['admin', 'assignments', 'edit'], null, ['admin', 'creator', 'manager']),
    view: isAllowed(['admin', 'assignments', 'view'], null, ['admin', 'creator', 'manager']),
    archive: isAllowed(['admin', 'assignments', 'archive'], null, ['admin', 'creator', 'manager']),
    complete: isAllowed(['admin', 'assignments', 'complete'], null, [
      'admin',
      'creator',
      'manager',
    ]),
  };

  const {
    markAssignmentsCompletedMutation,
    sendAssignmentsReminderMutation,
    archiveAssignmentsMutation,
  } = useWorkflowActions();

  const assignmentActionPayload = useMemo(
    () => ({
      assignmentIds: Object.keys(recipientSelection),
    }),
    [recipientSelection],
  );

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const { data, refetch, isFetching } = useListAssignmentRecipients(
    pagination,
    isInstance ? instanceId : id,
    isInstance,
  );

  useEffect(() => () => setRecipientSelection({}), []);

  const resetOnSuccess = () => {
    setRecipientSelection({});

    refetch();
  };

  const getRowId = useCallback((row: any) => row.node.id, []);

  const columnHelper = createColumnHelper<AssignmentEdge>();

  const columns = useMemo<ColumnDef<AssignmentEdge, any>[]>(
    () => [
      columnHelper.display({
        id: 'checkbox',
        size: 'auto' as unknown as number,
        cell: (info) => (
          <Checkbox
            variant="admin"
            onChange={info.row.getToggleSelectedHandler()}
            isChecked={info.row.getIsSelected()}
            isDisabled={!info.row.getCanSelect()}
          />
        ),
        header: (info) => (
          <Checkbox
            variant="admin"
            onChange={info.table.getToggleAllRowsSelectedHandler()}
            isChecked={info.table.getIsAllRowsSelected()}
          />
        ),
      }),

      columnHelper.accessor('node.assignee.fullName', {
        id: 'fullName',
        header: `${t('admin.assigments.tableHeader.name')}`,
        cell: (info) => <Text color="baseBlack">{info.getValue()}</Text>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.completionStatus', {
        id: 'completionStatus',
        header: `${t('admin.assigments.tableHeader.status')}`,
        cell: (info) => (
          <Text>{completionStatusTranslationMap[info.getValue() as CompletionStatus]}</Text>
        ),
      }),
      columnHelper.accessor('node.completionPercentage', {
        id: 'assignmentCompletionPercentage',
        header: `${t('admin.assigments.tableHeader.completion')}`,
        cell: (info) => {
          const percentComplete = Math.round(info.getValue());

          return (
            <HStack>
              <Progress variant="assignmentsTable" width="full" value={percentComplete} />

              <Text>{percentComplete}%</Text>
            </HStack>
          );
        },
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.createdAt', {
        id: 'instance',
        header: `${t('admin.assigments.tableHeader.instance')}`,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy h:mm a'),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.dueDate', {
        id: 'dueDate',
        header: `${t('admin.assigments.tableHeader.dueDate')}`,
        cell: (info) =>
          info.getValue()
            ? format(new Date(info.getValue()), 'MMM d, yyyy')
            : t('admin.assignments.tableInfo.na'),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.completedDate', {
        id: 'completedDate',
        header: `${t('admin.assigments.tableHeader.completedDate')}`,
        cell: (info) =>
          info.getValue()
            ? format(new Date(info.getValue()), 'MMM d, yyyy')
            : t('admin.assignments.tableInfo.notCompleted'),
        footer: (info) => info.column.id,
      }),
      columnHelper.display({
        id: 'actions',

        cell: (info) => {
          const payload = assignmentActionPayload;

          const { node } = info.row.original;

          const { id: assignmentId } = node;

          const isUnassigned = node.completionStatus === CompletionStatus.Unassigned;

          const isCompleted =
            node.completionStatus === CompletionStatus.CompletedLate ||
            node.completionStatus === CompletionStatus.CompletedOnTime;

          const actionPayload = {
            assignmentIds: [assignmentId],
          };

          return (
            <WarmNeutralTableActionsMenu
              isDisabled={isUnassigned || isCompleted}
              actions={[
                {
                  label: t('admin.assignments.tableActions.markComplete'),
                  shouldRender: permissions.complete,
                  onClick: () =>
                    markAssignmentsCompletedMutation
                      .mutateAsync(actionPayload)
                      .then(resetOnSuccess),
                },
                {
                  label: t('admin.assignments.tableActions.sendReminder'),
                  shouldRender: permissions.edit || permissions.view,
                  onClick: () =>
                    sendAssignmentsReminderMutation.mutateAsync(actionPayload).then(resetOnSuccess),
                },
                {
                  label: t('admin.assignments.tableActions.unassign'),
                  shouldRender: permissions.archive,
                  onClick: () =>
                    archiveAssignmentsMutation.mutateAsync(actionPayload).then(resetOnSuccess),
                },
              ]}
            />
          );
        },
      }),
    ],
    [],
  );

  return (
    <Box marginBottom={50}>
      <AssignmentsTable
        isLoading={isFetching}
        columns={columns}
        data={data?.edges}
        getRowId={getRowId}
        pagination={pagination}
        setPagination={setPagination}
        totalResultCount={data?.pageInfo.totalCount ?? 0}
        rowSelection={recipientSelection}
        setRowSelection={setRecipientSelection}
      />
    </Box>
  );
}
